import React, { useState } from 'react';
import { X, Loader2, CheckCircle2, Mail } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const NewsletterModal = ({ open, onClose }) => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('idle');
  const [errMsg, setErrMsg] = useState('');

  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    setStatus('sending');
    setErrMsg('');
    try {
      const res = await fetch(`${API_URL}/api/newsletter/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: name || null, email, source: 'footer_newsletter' }),
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j.detail || `Server returned ${res.status}`);
      }
      setStatus('sent');
    } catch (err) {
      setErrMsg(err.message || 'Could not subscribe. Please try again.');
      setStatus('error');
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" data-testid="newsletter-modal">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 sm:p-8">
        <button onClick={onClose} className="absolute top-4 right-4 text-gray-400 hover:text-gray-700" data-testid="newsletter-modal-close" aria-label="Close">
          <X size={20} />
        </button>
        {status === 'sent' ? (
          <div className="text-center py-6" data-testid="newsletter-modal-success">
            <CheckCircle2 size={40} className="text-emerald-500 mx-auto mb-3" />
            <h3 className="text-xl font-bold text-gray-900 mb-1" style={{ fontFamily: 'Georgia, serif' }}>Check your inbox ✨</h3>
            <p className="text-sm text-gray-600">We&apos;ve sent a confirmation link to <strong>{email}</strong>. Click it and you&apos;re in.</p>
          </div>
        ) : (
          <>
            <h3 className="text-2xl font-bold text-gray-900 mb-1" style={{ fontFamily: 'Georgia, serif' }}>HiBFF Newsletter</h3>
            <p className="text-sm text-gray-600 mb-5">Practical confidence tips for families — no spam, unsubscribe anytime.</p>
            <form onSubmit={submit} className="space-y-3">
              <input
                type="text"
                placeholder="Your name"
                value={name}
                onChange={e => setName(e.target.value)}
                className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm text-gray-800 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                data-testid="newsletter-modal-name"
              />
              <div className="relative">
                <Mail size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="email"
                  required
                  placeholder="your@email.com"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 rounded-xl border border-gray-200 text-sm text-gray-800 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  data-testid="newsletter-modal-email"
                />
              </div>
              {status === 'error' && (
                <p className="text-xs text-red-600" data-testid="newsletter-modal-error">{errMsg}</p>
              )}
              <button
                type="submit"
                disabled={status === 'sending'}
                className="w-full py-3 rounded-xl font-bold text-white bg-purple-600 hover:bg-purple-700 transition disabled:opacity-60 inline-flex items-center justify-center gap-2"
                data-testid="newsletter-modal-submit"
              >
                {status === 'sending' ? <Loader2 size={16} className="animate-spin" /> : null}
                Subscribe
              </button>
              <p className="text-[11px] text-gray-500 text-center">Double opt-in. We won&apos;t share your details.</p>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

export default NewsletterModal;
